import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, CircularProgress } from '@material-ui/core';
import { ErrorIcon } from '../../../icons';

import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import { useHistory } from 'react-router';
import FormSection from '../../../components/atom/FormSection';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import TimezoneSelector from '../../../components/atom/TimezoneSelector';
import moment from 'moment';

/**
 * This component shows the current date time (in US format) for a given timezone
 * Updates every second to give the user a clock feeling.
 * @param timezone
 * @constructor
 */
function TimezoneCurrentTimeTextField({ timezone }) {
    const [unixTime, setUnixTime] = useState(new Date().getTime());
    useEffect(() => {
        const t = setTimeout(() => {
            setUnixTime(new Date().getTime());
        }, 1000);
        return () => {
            clearTimeout(t);
        };
    }, [unixTime, timezone]);
    return (
        <TextField
            fullWidth
            label={`Current time in ${timezone}`}
            value={moment(unixTime)
                .tz(timezone)
                .format('YYYY-MM-DD HH:mm:ss')}
            disabled
        />
    );
}

function IslandsListPage() {
    const { islandId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();
    const [island, loading, error, setIsland] = useSwaggerClient('islands', 'getIsland', {
        islandId: parseInt(islandId, 10)
    });
    const modifyIsland = () => {
        doSwaggerCall('islands', 'updateIsland', {
            island: {
                name: island.name,
                timezone: island.timezone,
                vat: parseFloat(island.vat)
            },
            islandId: parseInt(islandId, 10)
        })
            .then(newIsland => {
                setIsland(newIsland);
                notifySuccess('Region modified!');
                history.push(`/admin/regions`);
            })
            .catch(err => {
                console.log(err);
                notifyError('Could not modify region!');
            });
    };

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }
    if (loading) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid className="bg-white activity-edit">
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                }}
            >
                <Grid container spacing={2}>
                    <AdminHeader title={`Region edit`} />
                    {!!error && (
                        <Grid
                            item
                            container
                            spacing={2}
                            xs={12}
                            style={{
                                color: theme.palette.error.main
                            }}
                            alignItems="center"
                        >
                            <Grid item>
                                <ErrorIcon color="error" />
                            </Grid>
                            <Grid item>
                                <span>{error.response.body.message}</span>
                            </Grid>
                        </Grid>
                    )}
                    <FormSection title="Generic information">
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                type="text"
                                value={island ? island.name : ''}
                                onChange={ev => setIsland({ ...island, name: ev.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Tax Rate"
                                fullWidth
                                required
                                type="text"
                                value={island ? island.vat : ''}
                                onChange={ev => setIsland({ ...island, vat: ev.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TimezoneSelector
                                label={'Timezone'}
                                value={island ? island.timezone : ''}
                                onChange={ev => setIsland({ ...island, timezone: ev.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            {island && island.timezone && (
                                <TimezoneCurrentTimeTextField timezone={island.timezone} />
                            )}
                        </Grid>
                    </FormSection>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={!island.name}
                            onClick={() => {
                                modifyIsland();
                            }}
                        >
                            Save
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => history.push(`/admin/regions`)}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
}

export default IslandsListPage;
